import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../store';
import features from './image-optimizer-features.json';
import {Subject} from "rxjs";
import {UserSubscription} from "../../../user/user-subscription";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics/google-analytics.service";
import {combineLatest} from "rxjs";
import {User} from "../../../user/user";
import {ActivatedRoute} from "@angular/router";
import {Website} from "../website";
import {Product} from "../product";
import {Config} from "../../../config/config";
import {ActivityAction} from "../../../activities/activity";
import {DbModalService} from "../../../modules/modal/modal";
import {Utils} from "../../../shared/utils/utils";
import {OneMonthFreeComponent} from "../../../shared/components/dialogs/one-month-free/one-month-free.component";

@Component({
    selector: 'db-image-optimizer',
    templateUrl: './image-optimizer.component.html',
    styleUrls: ['./image-optimizer.component.scss'],
    standalone: false
})
export class ImageOptimizerComponent implements OnInit, OnDestroy {

  @Input() fromBooster: boolean;
  public website: Website;
  public features: string[] = features;
  public user: User;
  public currentSubscription: UserSubscription;
  public imageOptimizerPlugin: Product;
  public loading: boolean;
  public demoUser: boolean = false;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _route: ActivatedRoute,
    private _store: Store<fromStore.DbStoreState>,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _modalService: DbModalService,
  ) {}

  ngOnInit() {
    this.website = this._route.snapshot.parent.data['website'] || this._route.snapshot.parent.parent.data['website'];
    this._getUserData();
    this._getImageOptimizer();
    this._imageOptimizerOngoingActivity();
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _getUserData(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        first(loaded => !!loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription)
        )),
        filter(([user, subscription]) => !!user && !!subscription),
        takeUntil(this._destroyStream$),
      )
      .subscribe(([user, subscription]) => {
        this.user = user;
        this.currentSubscription = subscription;
        this.demoUser = Utils.isDemoUser(subscription, user);
      });
  }

  private _getImageOptimizer(): void {
    this._store.select(fromStore.getWebsiteImageOptimizerPlugin(this.website.id))
      .pipe(takeUntil(this._destroyStream$))
      .subscribe((imageOptimizerPlugin): void => {
        if (imageOptimizerPlugin) {
          this.imageOptimizerPlugin = imageOptimizerPlugin;
          // this.isManagable = !this.website.isLocalhost && this.imageOptimizerPlugin && this.permissions.canManageContent;
        }
      });
  }

  private _imageOptimizerOngoingActivity(): void {
    this._store.select(fromStore.getImageOptimizerOngoingActivity(this.website.id, this.imageOptimizerPlugin?.slug))
      .pipe(takeUntil(this._destroyStream$))
      .subscribe((data): void => {
        if ( data && data.status === 'pending' && data.meta && data.meta.product_data && data.meta.product_data.slug && data.meta.product_data.slug === "image-optimizer-wd" && (data.action === "install_activate" || data.action === "activate")) {
          this.loading = true;
        }
        if (data && (data.status === 'done' || data.status === 'failed' || data.status === 'canceled')) {
          this.loading = false;
        }
      });
  }

  public enableImageOptimizerHandler() {
/*    if (this.user && this.user.subscriptionId === Config.defaultStarterPlanId) {
      return this._showCheckoutDialog();
    }*/
    if (this.imageOptimizerPlugin && !this.imageOptimizerPlugin.paidVersionInstalled) {
      this._installAndActivate();
    } else if (this.imageOptimizerPlugin && this.imageOptimizerPlugin.paidVersionInstalled && !this.imageOptimizerPlugin.active) {
      this._activate();
    }
  }

  public _installAndActivate(): void {
    this.loading = true;
    let network = 0;
    if (this.website.isNetwork || (!this.website.isNetwork && this.website.parentId)) {
      network = 1;
    }
    this._store.dispatch(new fromStore.InitWebsiteProductAction({
      website: this.website,
      products: [this.imageOptimizerPlugin],
      action: ActivityAction.INSTALL_ACTIVATE,
      network: network
    }));
  }

  private _activate(): void {
    this.loading = true;
    let network = 0;
    if (this.website.isNetwork || (!this.website.isNetwork && this.website.parentId)) {
      network = 1;
    }
    this._store.dispatch(new fromStore.InitWebsiteProductAction({
      website: this.website,
      products: [this.imageOptimizerPlugin],
      action: ActivityAction.ACTIVATE,
      network: network
    }));
  }

  private _showCheckoutDialog(): void {
    let analyticsData = {
      eventCategory: 'Free Upgrade Offer',
      eventAction: "Locked button click",
      eventLabel: "Image Optimizer"
    };
    const modal = this._modalService.open(OneMonthFreeComponent, {height: 600, width: 940});
    modal.componentInstance.mode = 'feature';
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }
}
